import { type IWarehouseProperty } from '@mahawi/eshop-common/src/types';
import { type RootState } from '@reducers';
import {
  Alert,
  Button,
  Col,
  Divider,
  Form,
  Row,
  Select,
  Skeleton,
  Space,
  Spin,
} from 'antd';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { getFilterOption, getFilterSort } from '../../fragments/select-filter';
import { useAppDispatch } from '../../hooks/use-app-dispatch';
import { downloaderLoadWarehouseProperties } from '../../reducers/downloader/reducer';
import { propertySaveWarehouseProperties } from '../../reducers/property/reducer';
import { SPACING } from '../../theme/tokens';
import BubbleComponent from '../bubble';
import Link from '../link';

interface IForm {
  warehousePropertiesRawNames: string[];
}

function WarehousePropertyForm(): React.ReactElement {
  const dispatch = useAppDispatch();
  const property = useSelector((s: RootState) => s.Property.property);
  const propertyInProcess = useSelector((s: RootState) => s.Property.inProcess);
  const downloaderWarehouseProperties = useSelector(
    (s: RootState) => s.Downloader.warehouse.properties,
  );
  const assignedRawNames = useSelector(
    (s: RootState) => s.Property.property?.warehouseProperty?.assignedRawNames,
  );

  const [form] = Form.useForm<IForm>();

  const onFinish = useCallback(
    ({ warehousePropertiesRawNames }: IForm): void => {
      if (!property) {
        return;
      }
      dispatch(
        propertySaveWarehouseProperties(
          property.code,
          warehousePropertiesRawNames,
        ),
      );
    },
    [dispatch, property],
  );

  const warehousePropertiesOptions = useMemo(
    (): { value: string; label: string }[] =>
      (downloaderWarehouseProperties ?? []).map(
        (wp: IWarehouseProperty): { value: string; label: string } => {
          let { name } = wp;
          if (wp.unit) {
            name += ` [${wp.unit}]`;
          }
          return { value: wp.rawName, label: name };
        },
      ),
    [downloaderWarehouseProperties],
  );

  const warehousePropertiesNames = useMemo((): Map<string, string> => {
    const result = new Map<string, string>();
    if (!downloaderWarehouseProperties || !assignedRawNames) {
      return result;
    }
    assignedRawNames.forEach((rawName: string): void => {
      const wp = downloaderWarehouseProperties.find(
        (p: IWarehouseProperty): boolean => p.rawName === rawName,
      );
      if (wp) {
        let { name } = wp;
        if (wp.unit) {
          name += ` [${wp.unit}]`;
        }
        result.set(rawName, name);
      }
    });
    return result;
  }, [assignedRawNames, downloaderWarehouseProperties]);

  return (
    <Form
      form={form}
      key={assignedRawNames?.join(',')}
      initialValues={{ warehousePropertiesRawNames: assignedRawNames ?? [] }}
      onFinish={(values: IForm): void => onFinish(values)}
    >
      <Row>
        <Col xs={24} md={8}>
          <Space size={SPACING.lg} align="start">
            <Button
              type="primary"
              htmlType="submit"
              disabled={propertyInProcess}
            >
              Submit
            </Button>
            <Spin size="small" spinning={propertyInProcess} />
          </Space>
        </Col>
      </Row>

      <Divider />

      <Row>
        <Col span={24}>
          <Form.Item name="warehousePropertiesRawNames">
            <Select
              mode="multiple"
              allowClear
              placeholder="Select warehouse properties"
              disabled={propertyInProcess}
              showSearch={{
                autoClearSearchValue: false,
                filterOption: getFilterOption,
                filterSort: getFilterSort,
              }}
              options={warehousePropertiesOptions}
            />
          </Form.Item>
        </Col>
      </Row>

      <Row>
        <Col span={24}>
          <div>
            <strong>Assigned warehouse properties</strong>
            {[...warehousePropertiesNames].map((item: [string, string]) => {
              const rawNameEncoded: string = encodeURIComponent(item[0]);
              return (
                <div key={item[0]}>
                  <Link
                    to={`/downloader/warehouse-property/${rawNameEncoded}`}
                    label={item[1]}
                    openInNewTab
                  />
                </div>
              );
            })}
          </div>
        </Col>
      </Row>
    </Form>
  );
}

function WarehouseProperty(): React.ReactElement {
  const dispatch = useAppDispatch();
  const downloaderWarehouseProperties = useSelector(
    (s: RootState) => s.Downloader.warehouse.properties,
  );

  useEffect((): void => {
    dispatch(downloaderLoadWarehouseProperties());
  }, [dispatch]);

  if (downloaderWarehouseProperties === undefined) {
    return <Skeleton active />;
  }

  if (downloaderWarehouseProperties === null) {
    return (
      <Alert title="Warehouse properties not found" type="error" showIcon />
    );
  }

  return (
    <BubbleComponent span={8}>
      <Space size={SPACING.lg} align="start" orientation="horizontal">
        <strong>Warehouse property</strong>
      </Space>
      <WarehousePropertyForm />
    </BubbleComponent>
  );
}

export default WarehouseProperty;
